import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  Tooltip,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { endOfMonth, format, startOfMonth, subMonths } from 'date-fns';
import { CSVLink } from 'react-csv';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TableHeader } from '../../components/Containers/ContainerTable';
import HeaderBottom from '../../components/super-admin/HeaderBottom';
import UserContext from '../../context/user';
import useContainerApiRoutes from '../../hooks/api/useContainerApiRoutes';
import useUtils from '../../hooks/useUtils';
import { Container } from '../../../types';
import { ConfirmationServiceContext } from '../../context/modal';
import SnackBarContext from '../../context/snackbar';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingTop: theme.spacing(3.25),
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
      paddingBottom: theme.spacing(5),
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.palette.grey[100],
      height: '100%',
    },
    tableWrap: {
      backgroundColor: 'white',
      borderRadius: 24,
      padding: theme.spacing(2),
      boxShadow: '0px 2px 16px 1px rgba(0, 0, 0, 0.12)',
    },
  }),
);

const ACCEPTED_STATUSES = ['assigned', 'internalUse', 'used'];
// One height for every control in a filter row, so the toggle group, chips
// and Export button line up.
const CONTROL_HEIGHT = 40;

type Outcome = 'all' | 'accepted' | 'declined' | 'cancelled';
type Filters = { from: string; to: string; bookingRef: string; customer: string; outcome: Outcome };

const isAccepted = (c: Container) => ACCEPTED_STATUSES.includes(c.status);
// A re-use that was cancelled after being requested/accepted (box dehired,
// booking fell through) — never billable.
const isCancelled = (c: Container) => c.status === 'cancelled';
const outcomeOf = (c: Container): 'Accepted' | 'Declined' | 'Cancelled' =>
  isCancelled(c) ? 'Cancelled' : isAccepted(c) ? 'Accepted' : 'Declined';
const customerName = (c: Container) =>
  c.user_requested?.companyInformation?.companyName || c.user_requested?.email || 'N/A';
const rowDate = (c: Container) => {
  const dateStr = c.acceptedAt || c.updated_at;
  return dateStr ? new Date(dateStr) : null;
};

const lastMonthRange = () => {
  const prev = subMonths(new Date(), 1);
  return { from: format(startOfMonth(prev), 'yyyy-MM-dd'), to: format(endOfMonth(prev), 'yyyy-MM-dd') };
};

// Customer name and outcome are applied client-side over the loaded rows
// (dates and booking ref go to the server).
const applyClientFilters = (containers: Container[], filters: Filters) => {
  const needle = filters.customer.trim().toLowerCase();
  return containers.filter((c) => {
    const outcome = outcomeOf(c);
    if (filters.outcome === 'accepted' && outcome !== 'Accepted') return false;
    if (filters.outcome === 'declined' && outcome !== 'Declined') return false;
    if (filters.outcome === 'cancelled' && outcome !== 'Cancelled') return false;
    if (needle && !customerName(c).toLowerCase().includes(needle)) return false;
    return true;
  });
};

// The same filter row is used on the page and inside the export window.
const FilterBar = ({
  filters,
  onChange,
  customerOptions,
  children,
}: {
  filters: Filters;
  onChange: (next: Filters) => void;
  customerOptions: string[];
  children?: React.ReactNode;
}) => {
  const set = (patch: Partial<Filters>) => onChange({ ...filters, ...patch });
  const lastMonth = lastMonthRange();
  const isLastMonth = filters.from === lastMonth.from && filters.to === lastMonth.to;
  const field = { backgroundColor: 'white', '& .MuiInputBase-root': { height: CONTROL_HEIGHT } };
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
      <Stack direction="row" spacing={1.5} alignItems="center">
        <TextField
          size="small"
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={filters.from}
          onChange={(e) => set({ from: e.target.value })}
          sx={field}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={filters.to}
          onChange={(e) => set({ to: e.target.value })}
          sx={field}
        />
        <Button
          size="small"
          variant="text"
          onClick={() => set(isLastMonth ? { from: '', to: '' } : lastMonth)}
          sx={{ textTransform: 'none', height: CONTROL_HEIGHT, whiteSpace: 'nowrap' }}
        >
          {isLastMonth ? 'Clear dates' : 'Last month'}
        </Button>
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <TextField
          size="small"
          label="Booking number"
          value={filters.bookingRef}
          onChange={(e) => set({ bookingRef: e.target.value })}
          sx={field}
        />
        <Autocomplete
          freeSolo
          size="small"
          options={customerOptions}
          inputValue={filters.customer}
          onInputChange={(_e, value) => set({ customer: value })}
          sx={{ ...field, minWidth: 220 }}
          renderInput={(params) => <TextField {...params} label="Customer" />}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={filters.outcome}
          onChange={(_e, value) => {
            if (value) set({ outcome: value });
          }}
          sx={{ backgroundColor: 'white', height: CONTROL_HEIGHT }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="accepted">Accepted</ToggleButton>
          <ToggleButton value="declined">Declined</ToggleButton>
          <ToggleButton value="cancelled">Cancelled</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {children}
    </Stack>
  );
};

// Row action that sits as a bare icon and slides its label out on hover, so
// the table stays quiet but the action is self-explanatory once pointed at.
const ExpandingButton = ({
  icon,
  label,
  color = 'inherit',
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  color?: 'inherit' | 'error';
  onClick: () => void;
}) => (
  <Button
    size="small"
    color={color}
    onClick={onClick}
    aria-label={label}
    sx={{
      textTransform: 'none',
      minWidth: 0,
      px: 1,
      color: color === 'error' ? 'error.main' : 'text.secondary',
      '& .expand-label': {
        maxWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        opacity: 0,
        // Collapse near-instantly so switching between two buttons never has
        // both labels wide at once (which reflowed the row mid-transition).
        transition: 'max-width 0.1s ease, opacity 0.08s ease, margin-left 0.1s ease',
        marginLeft: 0,
      },
      '&:hover .expand-label, &:focus-visible .expand-label': {
        maxWidth: 160,
        opacity: 1,
        marginLeft: 3,
        transition: 'max-width 0.25s ease, opacity 0.2s ease, margin-left 0.25s ease',
      },
    }}
  >
    {icon}
    <span className="expand-label">{label}</span>
  </Button>
);

const CSV_HEADERS = [
  { label: 'Outcome', key: 'outcome' },
  { label: 'Container #', key: 'container' },
  { label: 'Type', key: 'type' },
  { label: 'Shipping Line', key: 'shippingLine' },
  { label: 'Booking Ref', key: 'bookingRef' },
  { label: 'Customer', key: 'customer' },
  { label: 'Date', key: 'date' },
  { label: 'Cancellation reason', key: 'cancellationReason' },
];

const fileSlug = (name: string) =>
  name
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();

// Export window: its own copy of the filters (seeded from the page — with
// last month filled in when the page has no dates), its own data load, and a
// CSV per customer plus one for everything.
const ExportDialog = ({
  open,
  onClose,
  initialFilters,
  customerOptions,
}: {
  open: boolean;
  onClose: () => void;
  initialFilters: Filters;
  customerOptions: string[];
}) => {
  const { getReuseBilling } = useContainerApiRoutes();
  const { getContainerTypeName, getShippingLineName } = useUtils();
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [containers, setContainers] = useState<Container[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    // No date filter on the page: default the export to last calendar month
    // so a billing run doesn't accidentally export all history.
    const seeded =
      initialFilters.from || initialFilters.to ? initialFilters : { ...initialFilters, ...lastMonthRange() };
    setFilters(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    getReuseBilling({
      from: filters.from || undefined,
      to: filters.to || undefined,
      bookingRef: filters.bookingRef || undefined,
    }).then((result) => {
      if (cancelled) return;
      setContainers(result);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, filters.from, filters.to, filters.bookingRef, getReuseBilling]);

  const rows = useMemo(() => applyClientFilters(containers, filters), [containers, filters]);
  const byCustomer = useMemo(() => {
    const groups = new Map<string, Container[]>();
    rows.forEach((c) => groups.set(customerName(c), [...(groups.get(customerName(c)) || []), c]));
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  const toCsvRows = (list: Container[]) =>
    list.map((c) => {
      const d = rowDate(c);
      return {
        outcome: outcomeOf(c),
        container: c.containerCode || 'N/A',
        type: getContainerTypeName(c),
        shippingLine: getShippingLineName(c),
        bookingRef: c.shippingLineBookingRef || 'N/A',
        customer: customerName(c),
        date: d ? format(d, 'd MMM yyyy') : 'N/A',
        cancellationReason: c.reuseCancellation?.reason || '',
      };
    });
  const periodSlug = filters.from || filters.to ? `${filters.from || 'start'}_${filters.to || 'now'}` : 'all-dates';
  const countLabel = (list: Container[]) =>
    `${list.length} container${list.length === 1 ? '' : 's'} · ${list.filter(isAccepted).length} accepted`;

  const DownloadButton = ({ list, filename }: { list: Container[]; filename: string }) => (
    <CSVLink data={toCsvRows(list)} headers={CSV_HEADERS} filename={filename} style={{ textDecoration: 'none' }}>
      <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={list.length === 0}>
        Download
      </Button>
    </CSVLink>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Export reuse billing</DialogTitle>
      <DialogContent dividers>
        <FilterBar filters={filters} onChange={setFilters} customerOptions={customerOptions} />

        {byCustomer.length > 1 ? (
          <>
            <Typography variant="overline" color="textSecondary" display="block" mt={3}>
              Everything in one file
            </Typography>
            <List dense disablePadding>
              <ListItem
                sx={{ px: 0, py: 1 }}
                secondaryAction={
                  <DownloadButton list={rows} filename={`reuse-billing-all-customers-${periodSlug}.csv`} />
                }
              >
                <ListItemText
                  primary={`All customers (${byCustomer.length})`}
                  secondary={loading ? 'Loading…' : countLabel(rows)}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            </List>
          </>
        ) : null}

        <Typography variant="overline" color="textSecondary" display="block" mt={byCustomer.length > 1 ? 2 : 3}>
          {byCustomer.length > 1 ? 'One file per customer' : 'Customer'}
        </Typography>
        <List dense disablePadding>
          {byCustomer.map(([name, list], index) => (
            <ListItem
              key={name}
              divider={index < byCustomer.length - 1}
              sx={{ px: 0, py: 1 }}
              secondaryAction={
                <DownloadButton list={list} filename={`reuse-billing-${fileSlug(name)}-${periodSlug}.csv`} />
              }
            >
              <ListItemText primary={name} secondary={countLabel(list)} />
            </ListItem>
          ))}
          {byCustomer.length === 0 ? (
            <Typography variant="body2" color="textSecondary" py={2}>
              {loading ? 'Loading…' : 'Nothing to export for these filters.'}
            </Typography>
          ) : null}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const BillingReview = () => {
  const classes = useStyles();
  const { isLoggedIn } = useContext(UserContext);
  const { getContainerTypeName, getShippingLineName } = useUtils();
  const { getReuseBilling, cancelReuse, removeReuseRecord } = useContainerApiRoutes();
  const { showConfirmationModal, setOpenId } = useContext(ConfirmationServiceContext);
  const { showSnack } = useContext(SnackBarContext);

  const [containers, setContainers] = useState<Container[]>([]);
  const [filters, setFilters] = useState<Filters>({ from: '', to: '', bookingRef: '', customer: '', outcome: 'all' });
  const [exportOpen, setExportOpen] = useState(false);

  const load = useCallback(() => {
    getReuseBilling({
      from: filters.from || undefined,
      to: filters.to || undefined,
      bookingRef: filters.bookingRef || undefined,
    }).then(setContainers);
  }, [getReuseBilling, filters.from, filters.to, filters.bookingRef]);

  useEffect(() => {
    if (isLoggedIn) load();
  }, [isLoggedIn, load]);

  const rows = useMemo(() => applyClientFilters(containers, filters), [containers, filters]);
  const acceptedCount = rows.filter(isAccepted).length;

  // Admin cancels a confirmed re-use (e.g. the carrier dehired the box). The
  // shipping line and the customer are emailed; the row becomes Cancelled.
  const handleCancelReuse = async (container: Container) => {
    const res = await showConfirmationModal({
      title: `Cancel re-use of container #${container.containerCode}?`,
      contentText: `${customerName(container)} · booking ${
        container.shippingLineBookingRef || 'N/A'
      }\nThe shipping line will be emailed to cancel, the customer will be notified, and it will no longer count as a billable re-use.`,
      reasonLabel: 'Reason (sent to the shipping line and customer)',
      confirmLabel: 'Cancel re-use',
    });
    if (res && res.confirmed === true && res.reason) {
      await cancelReuse(container._id, res.reason);
      setOpenId(undefined);
      showSnack('Re-use cancelled — shipping line and customer notified.', 'success');
      load();
    }
  };

  // Admin permanently deletes a booking-request record (duplicates, test
  // rows, bad data). Nothing is emailed — that's what Cancel re-use is for.
  const handleRemoveRecord = async (container: Container) => {
    const res = await showConfirmationModal({
      title: `Remove booking request for container #${container.containerCode}?`,
      contentText: `${customerName(container)} · booking ${container.shippingLineBookingRef || 'N/A'} · ${outcomeOf(
        container
      )}\nThis permanently deletes the record — it disappears from Reuse Billing and the customer's lists, and no emails are sent. Use "Cancel re-use" instead if the shipping line or customer should be notified.`,
      confirmLabel: 'Remove permanently',
      destructive: true,
    });
    if (res && res.confirmed === true) {
      await removeReuseRecord(container._id);
      setOpenId(undefined);
      showSnack('Booking request removed.', 'success');
      load();
    }
  };

  // Distinct customer names across everything loaded (not just the filtered
  // rows) so the autocomplete offers every option.
  const customerOptions = useMemo(
    () => Array.from(new Set(containers.map(customerName))).sort((a, b) => a.localeCompare(b)),
    [containers],
  );

  return (
    <>
      <HeaderBottom subtext="Reuse Billing" showDateFilter={false} />
      <Box className={classes.container}>
        <Box mb={2}>
          <FilterBar filters={filters} onChange={setFilters} customerOptions={customerOptions}>
            <Box flex={1} />
            <Chip color="secondary" label={`${acceptedCount} accepted`} sx={{ height: 32 }} />
            <Chip variant="outlined" label={`${rows.length} total`} sx={{ height: 32 }} />
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => setExportOpen(true)}
              sx={{ height: 32, textTransform: 'none' }}
            >
              Export CSV
            </Button>
          </FilterBar>
        </Box>
        <ExportDialog
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          initialFilters={filters}
          customerOptions={customerOptions}
        />
        <Box className={classes.tableWrap} display="flex" flexDirection="column" flex={1} minHeight={0}>
          <TableContainer sx={{ flex: 1, minHeight: 0 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableHeader label="OUTCOME" />
                  <TableHeader label="CONTAINER #" />
                  <TableHeader label="TYPE" />
                  <TableHeader label="SHIPPING LINE" />
                  <TableHeader label="BOOKING REF" />
                  <TableHeader label="CUSTOMER" />
                  <TableHeader label="DATE" />
                  <TableHeader label="" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((container) => {
                  const outcome = outcomeOf(container);
                  const d = rowDate(container);
                  return (
                    <TableRow key={container._id}>
                      <TableCell>
                        <Tooltip
                          title={outcome === 'Cancelled' && container.reuseCancellation?.reason ? container.reuseCancellation.reason : ''}
                          arrow
                        >
                          <Chip
                            size="small"
                            color={outcome === 'Accepted' ? 'success' : outcome === 'Declined' ? 'error' : 'default'}
                            label={outcome}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>{container.containerCode || 'N/A'}</TableCell>
                      <TableCell>{getContainerTypeName(container)}</TableCell>
                      <TableCell>{getShippingLineName(container)}</TableCell>
                      <TableCell>{container.shippingLineBookingRef || 'N/A'}</TableCell>
                      <TableCell>{customerName(container)}</TableCell>
                      <TableCell>{d ? format(d, 'd MMM yyyy') : 'N/A'}</TableCell>
                      <TableCell align="right" sx={{ width: 200, minWidth: 200, maxWidth: 200 }}>
                        <Box display="flex" justifyContent="flex-end" overflow="hidden" whiteSpace="nowrap">
                          {outcome === 'Accepted' && ['assigned', 'internalUse'].includes(container.status) ? (
                            <ExpandingButton
                              icon={<CancelOutlinedIcon fontSize="small" />}
                              label="Cancel re-use"
                              onClick={() => handleCancelReuse(container)}
                            />
                          ) : null}
                          <ExpandingButton
                            icon={<DeleteOutlineIcon fontSize="small" />}
                            label="Remove"
                            color="error"
                            onClick={() => handleRemoveRecord(container)}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="textSecondary" align="center" py={4}>
                        No reuse containers match these filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
};

export default BillingReview;
